import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Mic, Square, X, Loader2, RotateCcw, Check, AlertTriangle, Keyboard, Plus, Minus, Pencil } from "lucide-react";
import Modal from "@/components/Modal";
import { Btn, Textarea, Badge } from "@/components/common";
import RecordPicker from "@/components/RecordPicker";
import { api, apiError } from "@/lib/api";

const MAX_SECONDS = 90;
const newRef = () => Math.random().toString(36).slice(2, 10);
const OP_ICON = { add: Plus, replace: RotateCcw, remove: Minus };

// Reviewed per-field voice updates to ONE existing contact (bound to canonical id).
export default function VoiceEditModal({ workspaceId, entityId, entityName, initialTranscript, onClose, onSaved }) {
  const [phase, setPhase] = useState(initialTranscript ? "processing" : "idle"); // idle|recording|processing|review|done|error
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [proposals, setProposals] = useState([]);
  const [clarifications, setClarifications] = useState([]);
  const [baseVersion, setBaseVersion] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const mediaRef = useRef(null); const chunksRef = useRef([]); const streamRef = useRef(null);
  const timerRef = useRef(null); const blobRef = useRef(null); const idemRef = useRef(newRef() + newRef());

  useEffect(() => {
    api.get(`/voice/status`).then((r) => setStatus(r.data)).catch(() => {});
    if (initialTranscript) { setTranscript(initialTranscript); propose(initialTranscript); }
    return () => stopAll(); // eslint-disable-next-line
  }, []);

  const stopAll = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); } catch {}
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const startRecording = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("This browser does not support audio recording. Type the update below instead."); setPhase("error"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mime = ""; for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) { if (MediaRecorder.isTypeSupported(m)) { mime = m; break; } }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => { blobRef.current = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" }); };
      mr.start(); setSeconds(0); setPhase("recording");
      timerRef.current = setInterval(() => setSeconds((s) => { if (s + 1 >= MAX_SECONDS) stopRecording(); return s + 1; }), 1000);
    } catch (e) {
      setError("Microphone permission was denied or unavailable. You can type the update below instead."); setPhase("error");
    }
  };

  const stopRecording = async () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const mr = mediaRef.current; if (!mr || mr.state === "inactive") return;
    await new Promise((res) => { mr.onstop = ((o) => () => { o && o(); res(); })(mr.onstop); mr.stop(); });
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setPhase("processing");
    try {
      const blob = blobRef.current;
      if (!blob || blob.size < 1000) throw { response: { data: { detail: "The recording was empty or silent. Please try again." } } };
      const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData(); fd.append("file", blob, `voice.${ext}`);
      const t = await api.post(`/voice/transcribe?workspace_id=${workspaceId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const txt = (t.data.transcript || "").trim();
      setTranscript(txt);
      if (!txt) { setError("No speech detected. Type the update, or record again."); setPhase("error"); return; }
      await propose(txt);
    } catch (err) { setError(apiError(err.response?.data?.detail) || "Transcription failed. Type the update, or retry."); setPhase("error"); }
  };

  const propose = async (txt) => {
    setPhase("processing");
    try {
      const { data } = await api.post(`/voice/edit/propose?workspace_id=${workspaceId}`, { entity_id: entityId, transcript: txt });
      setBaseVersion(data.base_version);
      setClarifications(data.clarifications || []);
      setProposals((data.proposals || []).map((p) => ({ ...p, decision: p.destructive ? "reject" : "accept", confirm_delete: false, editProposed: p.proposed })));
      setPhase("review");
    } catch (err) { setError(apiError(err.response?.data?.detail) || "Could not interpret the update. Edit the transcript and retry."); setPhase("review"); }
  };

  const startTyped = () => { setTranscript(""); setPhase("review"); setProposals([]); };
  const setP = (i, patch) => setProposals((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const apply = async (force = false) => {
    const accepted = proposals.filter((p) => p.decision === "accept").map((p) => {
      const d = { ...p.data };
      // reflect an edited proposed value back into the data for simple single-value kinds
      if (p.editProposed !== p.proposed) {
        if (["name", "description", "address", "tag"].includes(p.kind)) d.value = p.editProposed;
        else if (p.kind === "contact_method") d.value = p.editProposed;
        else if (p.kind === "field") d.value = p.editProposed;
        else if (p.kind === "note") d.body = p.editProposed;
        else if (p.kind === "importance") d.level = p.editProposed;
        else if (p.kind === "action") d.title = p.editProposed;
        else if (p.kind === "group") d.group_name = p.editProposed;
      }
      return { kind: p.kind, op: p.op, field: p.field, data: d, confirm_delete: p.op === "remove" ? !!p.confirm_delete : undefined };
    });
    if (accepted.length === 0) { toast.error("Accept at least one change, or cancel."); return; }
    const blocked = accepted.find((a) => a.op === "remove" && !a.confirm_delete);
    if (blocked) { toast.error(`Confirm the removal of "${blocked.field}" (tick the box) or reject it.`); return; }
    const unresolvedRel = proposals.find((p) => p.decision === "accept" && p.kind === "relationship" && p.op !== "remove" && !p.data?.target_id);
    if (unresolvedRel) { setP(proposals.indexOf(unresolvedRel), { linkError: "Choose who this connects to — select a contact, create one, or Me — or reject this link." }); toast.error("Resolve the relationship target before applying."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/voice/edit/apply?workspace_id=${workspaceId}`, { entity_id: entityId, idempotency_key: idemRef.current, base_version: baseVersion, accepted, force });
      setResult(data); setPhase("done"); onSaved && onSaved();
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm("This contact changed since you opened the review. Apply your changes on top of the latest version anyway?")) { apply(true); return; }
      } else { toast.error(apiError(err.response?.data?.detail) || "Could not save. Your review is preserved."); }
    } finally { setSaving(false); }
  };

  const mm = String(Math.floor(seconds / 60)); const ss = String(seconds % 60).padStart(2, "0");
  const acceptedCount = proposals.filter((p) => p.decision === "accept").length;

  return (
    <Modal open onClose={() => { stopAll(); onClose(); }} title={`Update ${entityName} by voice`} width={660} testid="voice-edit-modal">
      {status && !status.configured && (
        <div className="mb-4 p-3 text-xs" style={{ border: "1px solid var(--si-warning)", borderRadius: 2, color: "var(--si-warning)" }}>
          Voice is not configured on the server. You can still type the update below, or close this and use <strong>Edit Manually</strong>.
        </div>
      )}

      {phase === "idle" && (
        <div className="text-center py-6">
          <button data-testid="ve-record-btn" onClick={startRecording} disabled={status && !status.configured}
            className="mx-auto flex items-center justify-center transition-transform duration-200 hover:scale-105 disabled:opacity-40"
            style={{ width: 92, height: 92, borderRadius: "50%", background: "var(--si-teal)", border: "2px solid var(--si-teal-light)" }}>
            <Mic size={36} color="#fff" strokeWidth={1.5} />
          </button>
          <div className="mt-5 text-sm" style={{ color: "var(--si-text)" }}>Speak the changes for <strong>{entityName}</strong>.</div>
          <div className="mt-2 text-xs max-w-md mx-auto" style={{ color: "var(--si-text-2)" }}>
            e.g. "Her new phone is 570-555-0199. She now works at Riverton Realty. Add a note that we discussed referrals. Set importance to high." You review every change before saving.
          </div>
          <div className="mt-5"><Btn variant="ghost" onClick={startTyped} data-testid="ve-type-instead"><Keyboard size={14} /> Type instead</Btn></div>
        </div>
      )}

      {phase === "recording" && (
        <div className="text-center py-8">
          <div className="mx-auto flex items-center justify-center blink" style={{ width: 92, height: 92, borderRadius: "50%", background: "rgba(239,68,68,0.15)", border: "2px solid var(--si-danger)" }}><Mic size={36} color="#EF4444" /></div>
          <div className="mt-5 font-mono text-2xl" style={{ color: "var(--si-text)" }}>{mm}:{ss}</div>
          <div className="si-label mt-1">recording… (auto-stops at {MAX_SECONDS}s) — the saved transcript is produced after you stop</div>
          <div className="mt-6 flex gap-3 justify-center">
            <Btn variant="primary" onClick={stopRecording} data-testid="ve-stop-btn"><Square size={15} /> Stop & review</Btn>
            <Btn variant="danger" onClick={() => { stopAll(); setPhase("idle"); }}><X size={15} /> Cancel</Btn>
          </div>
        </div>
      )}

      {phase === "processing" && (
        <div className="text-center py-16"><Loader2 size={30} className="mx-auto animate-spin" style={{ color: "var(--si-teal-light)" }} /><div className="mt-4 text-sm font-mono" style={{ color: "var(--si-silver)" }}>Transcribing & preparing changes…</div></div>
      )}

      {phase === "error" && (
        <div className="py-6">
          <div className="flex items-start gap-2 p-3 mb-4" style={{ border: "1px solid var(--si-danger)", borderRadius: 2 }}><AlertTriangle size={16} style={{ color: "var(--si-danger)" }} /><div className="text-sm" style={{ color: "var(--si-text)" }}>{error}</div></div>
          <div className="flex gap-2 justify-center"><Btn variant="secondary" onClick={() => setPhase("idle")}><RotateCcw size={14} /> Record again</Btn><Btn variant="primary" onClick={startTyped}><Keyboard size={14} /> Type the update</Btn></div>
        </div>
      )}

      {phase === "review" && (
        <div className="space-y-4 max-h-[66vh] overflow-y-auto pr-1">
          <div>
            <div className="si-label mb-1">Transcript (editable)</div>
            <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="ve-transcript" placeholder="Type or dictate the changes…" />
            {transcript && <div className="mt-2"><Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => propose(transcript)} data-testid="ve-reextract"><RotateCcw size={12} /> Find changes from transcript</Btn></div>}
          </div>

          {clarifications.length > 0 && (
            <div className="p-3 text-xs" style={{ border: "1px solid var(--si-warning)", borderRadius: 2, color: "var(--si-warning)" }} data-testid="ve-clarifications">
              Needs clarification: {clarifications.join(" ")}
            </div>
          )}

          {proposals.length === 0 ? (
            <div className="text-sm text-center py-4" style={{ color: "var(--si-text-2)" }}>No changes detected yet. Edit the transcript and press “Find changes”, or close and use Edit Manually.</div>
          ) : (
            <div className="space-y-2">
              <div className="si-label">Proposed changes — accept, edit, or reject each. Nothing saves until you press Apply.</div>
              {proposals.map((p, i) => {
                const Icon = OP_ICON[p.op] || Pencil;
                return (
                  <div key={p.id} data-testid={`ve-proposal-${i}`} className="p-3" style={{ border: "1px solid var(--si-border)", borderRadius: 2, background: "var(--si-surface)", opacity: p.decision === "reject" ? 0.55 : 1 }}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="flex items-center gap-1.5 text-sm" style={{ color: "var(--si-text)" }}>
                        <Icon size={13} /> <strong>{p.field}</strong>
                        <Badge value={p.op === "remove" ? "disputed" : p.op === "replace" ? "uncertain" : "confirmed"}>{p.op}</Badge>
                      </span>
                      <div className="flex gap-1">
                        <button onClick={() => setP(i, { decision: "accept" })} data-testid={`ve-accept-${i}`} className="text-xs px-2 py-0.5" style={{ border: `1px solid ${p.decision === "accept" ? "var(--si-success)" : "var(--si-border)"}`, color: p.decision === "accept" ? "var(--si-success)" : "var(--si-text-2)", borderRadius: 2 }}>Accept</button>
                        <button onClick={() => setP(i, { decision: "reject" })} data-testid={`ve-reject-${i}`} className="text-xs px-2 py-0.5" style={{ border: `1px solid ${p.decision === "reject" ? "var(--si-danger)" : "var(--si-border)"}`, color: p.decision === "reject" ? "var(--si-danger)" : "var(--si-text-2)", borderRadius: 2 }}>Reject</button>
                      </div>
                    </div>
                    <div className="text-xs grid grid-cols-2 gap-2">
                      <div><span className="si-label">current</span><div style={{ color: "var(--si-text-2)" }}>{p.current}</div></div>
                      <div><span className="si-label">proposed</span>
                        <input value={p.editProposed} onChange={(e) => setP(i, { editProposed: e.target.value })} data-testid={`ve-edit-${i}`}
                          className="w-full px-2 py-1 text-sm" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2, color: "var(--si-text)" }} />
                      </div>
                    </div>
                    {p.excerpt && <div className="si-label mt-1.5" style={{ fontStyle: "italic" }}>“{p.excerpt}”</div>}
                    {p.kind === "relationship" && p.op !== "remove" && p.decision === "accept" && (
                      <div className="mt-2" data-testid={`ve-rel-resolve-${i}`}>
                        <RecordPicker workspaceId={workspaceId} label={`Connect ${entityName} to whom?`} excludeId={entityId}
                          value={p.data?.target_id ? { id: p.data.target_id, name: p.data.target_label || "selected" } : null}
                          onChange={(rec) => setP(i, { data: { ...p.data, target_id: rec ? rec.id : undefined, target_label: rec ? rec.name : undefined }, linkError: "" })}
                          allowMe allowUnset={false} required error={p.linkError || ""} testid={`ve-rel-picker-${i}`} />
                      </div>
                    )}
                    {p.op === "remove" && p.decision === "accept" && (
                      <label className="flex items-center gap-2 text-xs mt-2" style={{ color: "var(--si-danger)" }} data-testid={`ve-confirm-remove-${i}`}>
                        <input type="checkbox" checked={!!p.confirm_delete} onChange={(e) => setP(i, { confirm_delete: e.target.checked })} /> Yes, remove this — I understand it changes the record.
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-between items-center pt-1">
            <Btn variant="ghost" onClick={() => { stopAll(); onClose(); }} data-testid="ve-cancel">Cancel (nothing saved)</Btn>
            <Btn variant="primary" onClick={() => apply(false)} disabled={saving || acceptedCount === 0} data-testid="ve-apply"><Check size={15} /> {saving ? "Saving…" : `Apply ${acceptedCount} change${acceptedCount === 1 ? "" : "s"}`}</Btn>
          </div>
        </div>
      )}

      {phase === "done" && result && (
        <div className="py-8 text-center">
          <div className="mx-auto flex items-center justify-center mb-4" style={{ width: 54, height: 54, borderRadius: "50%", background: "rgba(16,185,129,0.15)", border: "1px solid var(--si-success)" }}><Check size={24} style={{ color: "var(--si-success)" }} /></div>
          <div className="text-sm" style={{ color: "var(--si-text)" }}>Applied {result.applied.length} change{result.applied.length === 1 ? "" : "s"} to {entityName}.</div>
          {result.applied.length > 0 && <div className="text-xs mt-1" style={{ color: "var(--si-text-2)" }}>{result.applied.map((a) => a.field).join(", ")}</div>}
          {result.skipped?.length > 0 && <div className="text-xs mt-1" style={{ color: "var(--si-warning)" }}>Skipped: {result.skipped.map((s) => `${s.field} (${s.reason})`).join(", ")}</div>}
          <div className="mt-6"><Btn variant="primary" onClick={() => { stopAll(); onClose(); }} data-testid="ve-close">Done</Btn></div>
        </div>
      )}
    </Modal>
  );
}
